import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

export default class NetworkRetryService extends Service {
  @tracked retryAttempts = new Map();

  /**
   * Executes a GraphQL operation with retry logic
   * @param {Function} operation - The async operation to retry
   * @param {Object} options - Retry configuration
   * @returns {Promise<any>} The result of the operation
   */
  async withRetry(operation, options = {}) {
    const {
      maxRetries = 3,
      baseDelay = 1000,
      maxDelay = 10000,
      backoffFactor = 2,
      retryCondition = this.defaultRetryCondition,
      onRetry = null,
      operationId = null,
    } = options;

    const id = operationId || this.generateOperationId();
    let attempt = 0;
    let lastError;

    while (attempt <= maxRetries) {
      try {
        const result = await operation();

        // Reset retry count on success
        this.retryAttempts.delete(id);

        return result;
      } catch (error) {
        lastError = error;
        attempt++;

        // Update retry attempts tracking
        this.retryAttempts.set(id, attempt);

        // Check if we should retry
        if (attempt > maxRetries || !retryCondition(error, attempt)) {
          this.retryAttempts.delete(id);
          throw error;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          baseDelay * Math.pow(backoffFactor, attempt - 1),
          maxDelay,
        );

        // Add jitter to prevent thundering herd
        const jitteredDelay = delay * (0.5 + Math.random() * 0.5);

        // Notify about retry attempt
        if (onRetry) {
          onRetry(error, attempt, jitteredDelay);
        }

        // Wait before retrying
        await this.delay(jitteredDelay);
      }
    }

    this.retryAttempts.delete(id);
    throw lastError;
  }

  /**
   * Default retry condition for network errors
   * @param {Error} error - The error that occurred
   * @param {number} attempt - Current attempt number
   * @returns {boolean} Whether to retry the operation
   */
  defaultRetryCondition(error, attempt) {
    // Don't retry on client errors (4xx) except for specific cases
    if (error.status >= 400 && error.status < 500) {
      // Retry on rate limiting or timeout
      return error.status === 429 || error.status === 408;
    }

    // Retry on server errors (5xx)
    if (error.status >= 500) {
      return true;
    }

    // Retry on network errors
    if (
      error.name === 'NetworkError' ||
      (error.name === 'TypeError' && error.message.includes('fetch')) ||
      error.message?.includes('fetch')
    ) {
      return true;
    }

    // Retry on timeout errors
    if (error.name === 'AbortError' || error.message?.includes('timeout')) {
      return true;
    }

    return false;
  }

  /**
   * GraphQL-specific retry wrapper
   * @param {Function} graphqlQuery - The GraphQL query function
   * @param {Object} variables - Query variables
   * @param {Object} options - Retry configuration
   * @returns {Promise<any>} Query result
   */
  async retryGraphQL(graphqlQuery, variables = {}, options = {}) {
    const graphqlRetryCondition = (error, attempt) => {
      // Don't retry GraphQL errors (usually schema/validation issues)
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        return false;
      }

      // Use default retry logic for network errors
      return this.defaultRetryCondition(error, attempt);
    };

    return this.withRetry(() => graphqlQuery(variables), {
      ...options,
      retryCondition: options.retryCondition || graphqlRetryCondition,
      operationId:
        options.operationId || `graphql-${Date.now()}-${Math.random()}`,
    });
  }

  /**
   * Gets the current retry attempt for an operation
   * @param {string} operationId - The operation identifier
   * @returns {number} Current attempt number
   */
  getRetryAttempt(operationId) {
    return this.retryAttempts.get(operationId) || 0;
  }

  /**
   * Cancels all ongoing retry attempts
   */
  cancelAllRetries() {
    this.retryAttempts.clear();
  }

  /**
   * Creates a delay promise
   * @param {number} ms - Delay in milliseconds
   * @returns {Promise<void>}
   */
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Generates a unique operation ID
   * @returns {string} Unique identifier
   */
  generateOperationId() {
    return `op-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Creates a retry helper with preset configuration
   * @param {Object} defaultOptions - Default retry configuration
   * @returns {Function} Configured retry function
   */
  createRetryHelper(defaultOptions = {}) {
    return (operation, options = {}) => {
      return this.withRetry(operation, { ...defaultOptions, ...options });
    };
  }
}
